import { PageHeader } from "@/components/PageHeader";
import NotFound404 from "@/pages/NotFound404";
import NotFoundRedirect from "@/pages/NotFoundRedirect";
import {
  createRootRoute,
  Outlet,
  useLocation,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { login, logout, selectUserType } from "@/store/authSlice";
import { useAppDispatch } from "@/store";
import { getCurrentUser } from "aws-amplify/auth";
import { Helmet } from "@dr.pogodin/react-helmet";

const pathsWithNoHeader = ["/property-intake"];
const pathsWithNoAuth = ["/", "/faq", "/login", /^\/assessments/];

const isNoAuthPath = (pathname: string): boolean =>
  pathsWithNoAuth.some((path) =>
    typeof path === "string" ? path === pathname : path.test(pathname)
  );

export const Route = createRootRoute({
  component: () => {
    const userType = useSelector(selectUserType);
    const dispatch = useAppDispatch();
    const [initialized, setInitialized] = useState(false);
    const router = useRouterState();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const showHeader = !pathsWithNoHeader.includes(pathname);

    useEffect(() => {
      getCurrentUser()
        .then(async (user) => {
          if (user) await dispatch(login(user));
          else dispatch(logout());
        })
        .catch(() => {
          console.log(pathname);
          if (!isNoAuthPath(pathname)) navigate({ to: "/" });
        })
        .finally(() => {
          setInitialized(true);
        });
    }, []);

    useEffect(() => {
      if (userType === "system_admin") {
        document.body.classList.add("admin-user");
      } else if (userType === "support_staff") {
        document.body.classList.add("support-user");
      } else {
        document.body.classList.remove("admin-user");
        document.body.classList.remove("support-user");
      }
    }, [userType]);

    return (
      <>
        <Helmet
          titleTemplate="SmartCMA - %s"
          title="Comprehensive Property Reports"
          prioritizeSeoTags={true}
        />
        <Box height="100%" display="flex" flexDirection="column">
          {showHeader && (
            <Box
              maxWidth={{
                xs: "640px",
                sm: "768px",
                md: "1024px",
                lg: "1280px",
                xl: "1536px",
              }}
              width={"100%"}
              mx={"auto"}
            >
              <PageHeader />
            </Box>
          )}

          <Box
            height={showHeader ? "calc(100% - var(--spacing) * 24)" : "100vh"}
          >
            {!initialized ? null : <Outlet />}
          </Box>

          <TanStackRouterDevtools />
        </Box>
      </>
    );
  },
  notFoundComponent: import.meta.env.MODE.includes("local")
    ? NotFound404
    : NotFoundRedirect,
});
